import React, { useState } from 'react';
import { Check } from 'lucide-react';
import SectionEyebrow from '../components/SectionEyebrow';
import OpenTableWidget from '../components/OpenTableWidget';
import MenuModal from '../components/MenuModal';
import { usePage, img } from '../lib/content';
import SmartMedia from '../components/SmartMedia';
import Seo from '../components/Seo';

const Steakhouse = () => {
  const c = usePage('steakhouse');
  const features = Array.isArray(c.tomahawkFeatures) ? c.tomahawkFeatures : [];
  const [menu, setMenu] = useState({ open: false, title: '', items: [] });

  const openMenu = (title, items) => setMenu({ open: true, title, items: Array.isArray(items) ? items : [] });
  const closeMenu = () => setMenu((m) => ({ ...m, open: false }));

  return (
    <div className="bg-[#f7f1e3]">
      <Seo title="The Steakhouse at Millpond Gatherings · Dry-Aged Steaks · Northford, CT" description="Dry-aged steaks and wood-fired hearth dining inside a historic 1700s mill in Northford, CT. Fine dining serving Wallingford, Hamden, Durham, Haddam, Chester, North Haven & New Haven County. Reserve your table at The Steakhouse." keywords="steakhouse Northford CT, best steakhouse New Haven County, dry-aged steak Connecticut, USDA prime steak, wood-fired hearth dining, fine dining restaurant CT, date night restaurant Northford, ribeye filet New York strip, romantic dinner Connecticut, upscale restaurant North Haven Wallingford Hamden Durham Haddam Chester, historic mill restaurant CT, chophouse Connecticut" path="/steakhouse" breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'The Steakhouse', path: '/steakhouse' }]} />
      {menu.open && <MenuModal open onClose={closeMenu} title={menu.title} menus={menu.items} />}

      {/* HERO */}
      <section className="relative h-[88vh] min-h-[640px] w-full overflow-hidden">
        <SmartMedia src={c.heroImage} alt="Steakhouse" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/30 to-black/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#f7f1e3] via-transparent to-transparent" style={{ backgroundSize: '100% 30%', backgroundRepeat: 'no-repeat', backgroundPosition: 'bottom' }} />
        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-24 lg:pb-32">
          <SectionEyebrow light>{c.heroEyebrow}</SectionEyebrow>
          <h1 className="font-serif text-[#f7f1e3] text-[64px] md:text-[96px] lg:text-[140px] tracking-[-0.02em] mt-4 leading-none">
            {c.heroTitle}
          </h1>
        </div>
      </section>

      {/* EXPERIENCE */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-6">
            <SectionEyebrow>{c.expEyebrow}</SectionEyebrow>
            <h2 className="font-serif text-[#942222] text-[40px] md:text-[56px] lg:text-[64px] leading-[1.05] tracking-[-0.02em] mt-5">
              {c.expTitle}
            </h2>
          </div>
          <div className="lg:col-span-6 space-y-5 text-[#942222]/85 text-[16px] leading-relaxed">
            <p>{c.expBody1}</p>
            <p>{c.expBody2}</p>
            <button
              type="button"
              onClick={() => openMenu('The Steakhouse · Menus', c.menus)}
              data-testid="steakhouse-view-menu"
              className="mt-4 inline-flex items-center gap-2 text-[#942222] font-medium text-[13px] tracking-[0.18em] uppercase border-b border-[#942222] pb-1 hover:gap-3 transition-all"
            >
              {c.menuLabel}
            </button>
          </div>
        </div>
      </section>

      {/* Image break */}
      <section className="px-6 lg:px-10">
        <div className="max-w-[1400px] mx-auto aspect-[16/8] overflow-hidden">
          <SmartMedia src={c.interiorImage} alt="Steakhouse interior" className="w-full h-full object-cover" />
        </div>
      </section>

      {/* TOMAHAWK */}
      <section className="py-24 lg:py-32 bg-[#942222] text-[#f1ead9]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-6">
            <SectionEyebrow light>{c.tomahawkEyebrow}</SectionEyebrow>
            <h2 className="font-serif text-[44px] md:text-[64px] lg:text-[80px] leading-[1.03] tracking-[-0.02em] mt-5">
              {c.tomahawkTitle}
            </h2>
          </div>
          <div className="lg:col-span-6">
            <p className="text-[#f1ead9]/85 text-[17px] leading-relaxed">
              {c.tomahawkBody}
            </p>
            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {features.map((f, i) => (
                <li key={i} className="flex items-center gap-3 text-[#f1ead9]/90 text-[15px]">
                  <span className="w-8 h-8 rounded-full border border-[#e2b95a] flex items-center justify-center">
                    <Check size={14} className="text-[#e2b95a]" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => openMenu(c.tomahawkTitle || 'Tomahawk Thursday', c.tomahawkMenus)}
              data-testid="tomahawk-view-menu"
              className="mt-10 inline-flex items-center gap-2 px-7 py-3 border border-[#e2b95a] text-[#e2b95a] text-[11px] tracking-[0.28em] uppercase font-semibold hover:bg-[#e2b95a] hover:text-[#942222] transition-colors"
            >
              {c.tomahawkMenuLabel || 'View the Menu →'}
            </button>
          </div>
        </div>
      </section>

      {/* OPENTABLE INLINE BANNER */}
      <section className="py-20 lg:py-24 bg-[#efe6d0]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 text-center">
          <SectionEyebrow>{c.bookEyebrow}</SectionEyebrow>
          <h2 className="font-serif text-[#942222] text-[36px] md:text-[48px] lg:text-[56px] leading-[1.05] tracking-[-0.02em] mt-5">
            {c.bookTitle}
          </h2>
          <p className="mt-5 text-[#942222]/75 text-[15px] max-w-md mx-auto">
            {c.bookBody}
          </p>
          <div className="mt-10 flex justify-center">
            <OpenTableWidget rid={c.opentableRid} theme="standard" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Steakhouse;
